import React from 'react';
import L from 'leaflet';
import mapMarkerIconEntOrange from '../images/mapMarkerIconEntOrange.png';

const Map = React.forwardRef(
  (
    {
      center = { lat: 0, lng: 0 },
      zoom = 1,
      scrollWheelZoom = true,
      markers = null,
      markerCoordinates = null,
      popupContent = null,
      zoomOutLevel = 6,
      className,
    },
    ref,
  ) => {
    const leafletMapRef = React.useRef(null);

    React.useImperativeHandle(ref, () => ({
      zoomInTo(coords) {
        leafletMapRef.current.flyTo(coords, 14, {
          animate: true,
          duration: 1.5,
        });
      },
      zoomOutTo(coords) {
        leafletMapRef.current.flyTo(coords, zoomOutLevel, {
          animate: true,
          duration: 1.5,
        });
      },
    }));

    React.useEffect(() => {
      // create a Leaflet map
      leafletMapRef.current = L.map('mapid', {
        scrollWheelZoom,
        zoomControl: false,
        attributionControl: false,
      }).setView([center.lat, center.lng], zoom);

      // move zoom control to top right to aviod being blocked by text layer in 768px
      L.control.zoom({ position: 'topleft' }).addTo(leafletMapRef.current);

      // use Mapbox tiles to have a better street view
      L.tileLayer(
        'https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}',
        {
          // attribution:
          //   'data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
          maxZoom: 18,
          id: 'techsico/ckm4zvgzz1vd017pdj4oex2fq',
          tileSize: 512,
          zoomOffset: -1,
          accessToken: process.env.REACT_APP_MAPBOX_ACCESS_TOKEN,
        },
      ).addTo(leafletMapRef.current);

      // define our own marker icon
      const markerIcon = L.icon({
        iconUrl: mapMarkerIconEntOrange,
        iconSize: [25, 41],
        iconAnchor: [12.5, 41],
        popupAnchor: [0, -35],
      });

      if (markers && Array.isArray(markers)) {
        // add multiple markers and popups to the map
        markers
          .filter(({ coordinates }) => !!coordinates)
          .forEach(({ coordinates, address }) => {
            L.marker(coordinates, { icon: markerIcon })
              .addTo(leafletMapRef.current)
              .bindPopup(address);
          });
      } else if (markerCoordinates) {
        // add marker to the map
        const marker = L.marker(markerCoordinates, { icon: markerIcon }).addTo(
          leafletMapRef.current,
        );
        // add popup to the marker
        if (popupContent) {
          marker.bindPopup(popupContent);
        }
      }
      // cleanup
      return () => {
        if (leafletMapRef.current && leafletMapRef.current.remove) {
          leafletMapRef.current.off();
          leafletMapRef.current.remove();
        }
      };
    }, [
      center,
      zoom,
      scrollWheelZoom,
      markers,
      markerCoordinates,
      popupContent,
    ]);

    const [isAttributionShowing, setIsAttributionShowing] = React.useState(
      false,
    );

    return (
      <div
        className="relative w-full h-full"
        onMouseOver={() => {
          setIsAttributionShowing(true);
        }}
        onFocus={() => {
          setIsAttributionShowing(true);
        }}
        onMouseOut={() => {
          setIsAttributionShowing(false);
        }}
        onBlur={() => {
          setIsAttributionShowing(false);
        }}
      >
        <div id="mapid" className={`z-10 h-full ${className}`} />
        {/* customized attribution */}
        <div
          className={`absolute bottom-0 right-0 z-20 rounded-br-2xl text-attribution px-1 ${
            isAttributionShowing ? 'animate-fadeIn' : 'animate-fadeOut'
          }`}
          style={{
            fontFamily: '"Helvetica Neue", Arial, Helvetica, sans-serif',
          }}
        >
          <a
            href="https://leafletjs.com"
            title="A JS library for interactive maps"
            className="hover:underline"
            style={{ color: '#0078A8' }}
          >
            Leaflet
          </a>
          <span> | Data © </span>
          <a
            href="https://www.openstreetmap.org/copyright"
            className="hover:underline"
            style={{ color: '#0078A8' }}
          >
            OpenStreetMap
          </a>
          <span>, imagery © </span>
          <a
            href="https://www.mapbox.com/"
            className="hover:underline"
            style={{ color: '#0078A8' }}
          >
            Mapbox
          </a>
        </div>
      </div>
    );
  },
);

export default Map;
